import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import React, { useEffect, useMemo, useState } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import {
  Flex,
  Breadcrumb,
  Button,
  Row,
  Col,
  Typography,
  ConfigProvider,
  Tag,
} from 'antd'
import { theme as antdTheme } from 'antd'
import { MonitorExportButton } from './MonitorExportButton'
import {
  WHITE_COLOR,
  LIGHT_GRAY_COLOR,
  TRANSPARENT,
  BLACK_COLOR,
} from '../constants'
import {
  HomeOutlined,
  LeftOutlined,
  EllipsisOutlined,
  FilterOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import { useGetUsageData } from '../hooks/UsageMonitor'
import { fetchUsageMonitorConfig } from '../api/usage-monitor'
import { useCheckPermission } from '../hooks/Permissions'
import dayjs from 'dayjs'
import GroupedByUserGraph from './Widgets/GroupedByUserGraph'
import MostViewedSubsystem from './Widgets/MostViewedSubsystem'
import DailyActiveUsers from './Widgets/DailyActiveUsers'
import TimeSpentOnSubsystem from './Widgets/TimeSpentOnSubsystem'
import { FullScreenSubsystemWidget } from './Widgets/FullScreenSubsystemWidget'
import { requireAuthentication } from '../helpers/authentication'
import {
  getTimeAgoText,
  filterUsageEventsByDate,
  buildUserOptions,
  buildDateOptions,
  getUserIdentifierFromRecord,
} from './utils'
import ReportTimePicker from './ReportTimePicker'
import ActiveFiltersDropdown from './Widgets/ActiveFiltersDropdown'

const { Title } = Typography

const COMPONENT = 'component'
const SUBSYSTEM = 'subsystem'

export const UsageMonitor = requireAuthentication(
  injectIntl(() => {
    const [userIdentifier, setUserIdentifier] = useState('user_id')

    useEffect(() => {
      fetchUsageMonitorConfig().then((config) => {
        if (config?.user_identifier) {
          setUserIdentifier(config.user_identifier)
        }
      })
    }, [])
    const { defaultAlgorithm } = antdTheme
    const history = useHistory()
    const checkPermission = useCheckPermission()

    if (!checkPermission('page-usage-monitor:get')) {
      return (
        <div>
          Access Denied: You do not have permission to view usage monitor.
        </div>
      )
    }

    const filterTimeWindow = [
      { label: '1d', value: '24' },
      { label: '1w', value: '168' },
      { label: 'Custom', value: 'custom' },
    ]
    const [startTimeFilter, setStartTimeFilter] = useState(null)
    const [endTimeFilter, setEndTimeFilter] = useState(null)
    const [sortedData, setSortedData] = useState({})
    const { allUsageData, isLoading, refetch } = useGetUsageData(
      startTimeFilter,
      endTimeFilter,
    )
    const [lastUpdatedTime, setLastUpdatedTime] = useState(new Date())
    const [isModalOpen, toggleModel] = useState(false)

    const [selectedDate, setSelectedDate] = useState(null)
    const [selectedUser, setSelectedUser] = useState(null)
    const [showDetailsOn, setShowDetailsOn] = useState(SUBSYSTEM)

    useEffect(() => {
      if (!selectedUser) setShowDetailsOn(SUBSYSTEM)
    }, [selectedUser])

    const filteredAllData = useMemo(() => {
      if (!selectedUser && !selectedDate) return sortedData
      const applyFilters = (events) => {
        let d = events || []
        if (selectedDate) d = filterUsageEventsByDate(d, selectedDate)
        if (selectedUser)
          d = d.filter(
            (r) =>
              getUserIdentifierFromRecord(r, userIdentifier) === selectedUser,
          )
        return d
      }
      return {
        ...sortedData,
        FUNCTIONAL_DIAGRAM_UNLOADED: applyFilters(
          sortedData['FUNCTIONAL_DIAGRAM_UNLOADED'],
        ),
        FUNCTIONAL_DIAGRAM_COMPONENT_UPDATED: applyFilters(
          sortedData['FUNCTIONAL_DIAGRAM_COMPONENT_UPDATED'],
        ),
        FUNCTIONAL_DIAGRAM_CIRCUIT_UPDATED: applyFilters(
          sortedData['FUNCTIONAL_DIAGRAM_CIRCUIT_UPDATED'],
        ),
        FUNCTIONAL_DIAGRAM_HARNESS_UPDATED: applyFilters(
          sortedData['FUNCTIONAL_DIAGRAM_HARNESS_UPDATED'],
        ),
        FUNCTIONAL_DIAGRAM_SUBSYSTEM_UPDATED: applyFilters(
          sortedData['FUNCTIONAL_DIAGRAM_SUBSYSTEM_UPDATED'],
        ),
      }
    }, [sortedData, selectedUser, selectedDate, userIdentifier])

    const dateOptions = useMemo(
      () => buildDateOptions(sortedData, 'FUNCTIONAL_DIAGRAM_UNLOADED'),
      [sortedData],
    )
    const userOptions = useMemo(
      () =>
        buildUserOptions(
          sortedData,
          [
            'FUNCTIONAL_DIAGRAM_UNLOADED',
            'FUNCTIONAL_DIAGRAM_COMPONENT_UPDATED',
            'FUNCTIONAL_DIAGRAM_CIRCUIT_UPDATED',
            'FUNCTIONAL_DIAGRAM_HARNESS_UPDATED',
            'FUNCTIONAL_DIAGRAM_SUBSYSTEM_UPDATED',
          ],
          userIdentifier,
        ),
      [sortedData, userIdentifier],
    )

    const filtersDropdown = (
      <ActiveFiltersDropdown
        filters={[
          {
            key: 'date',
            label: 'Date',
            value: selectedDate,
            options: dateOptions,
            onChange: setSelectedDate,
          },
          {
            key: 'user',
            label: 'User',
            value: selectedUser,
            options: userOptions,
            onChange: setSelectedUser,
          },
        ]}
      />
    )

    useEffect(() => {
      if (!isLoading && allUsageData) {
        setLastUpdatedTime(new Date())
        filterUserData()
      }
    }, [isLoading, allUsageData])

    const filterUserData = () => {
      const sortedDataCopy = {}
      allUsageData.forEach((data) => {
        if (sortedDataCopy[data.eventType])
          sortedDataCopy[data.eventType].push(data)
        else sortedDataCopy[data.eventType] = [data]
      })
      setSortedData(sortedDataCopy)
    }

    // Flattens each event object so all top-level and eventData keys become columns
    const flattenExportData = (dataArr) => {
      return dataArr.map((event) => {
        const { eventData = {}, ...rest } = event
        let loggedAtFormatted = rest.loggedAt
        if (loggedAtFormatted) {
          loggedAtFormatted = dayjs(loggedAtFormatted).format(
            'YYYY-MM-DD HH:mm:ss',
          )
        }
        return { ...rest, ...eventData, loggedAt: loggedAtFormatted }
      })
    }

    const handleMonitorExport = (exportType) => {
      // exportType: 'components' or 'subsystems'
      if (!Array.isArray(allUsageData)) {
        console.log('No usage data to export')
        return
      }
      let filtered = []
      if (exportType === 'components') {
        filtered = allUsageData.filter((e) =>
          String(e.eventType).toLowerCase().includes('component'),
        )
      } else if (exportType === 'subsystems') {
        filtered = allUsageData.filter((e) =>
          String(e.eventType).toLowerCase().includes('subsys'),
        )
      }
      const flattened = flattenExportData(filtered)
      const csv = Papa.unparse(flattened)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
      const filename =
        exportType === 'components'
          ? 'usage-components.csv'
          : 'usage-subsystems.csv'
      saveAs(blob, filename)
    }

    return (
      <Flex vertical id="usage-monitor-page">
        <Flex
          vertical={false}
          justify="flex-start"
          gap={10}
          className="reports-breadcrumb-wrapper"
        >
          <LeftOutlined onClick={() => history.push('/reports')} />
          <div>
            <Breadcrumb>
              <Breadcrumb.Item>
                <HomeOutlined />
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <FormattedMessage id="faultReport.header" />
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <FormattedMessage id="faultReport.subUsageLabel" />
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </Flex>
        <Flex className="reports-page-body" vertical={true}>
          <Flex
            vertical={false}
            className="usage-monitor-filter-panel"
            align="center"
            justify="flex-end"
          >
            <Flex
              vertical={false}
              className="usage-monitor-filter-panel-left"
              align="center"
              gap={16}
              justify="flex-end"
            >
              {filtersDropdown}
              <ReportTimePicker
                filterTimeWindow={filterTimeWindow}
                onFilterChange={(start, end) => {
                  setStartTimeFilter(start)
                  setEndTimeFilter(end)
                }}
                showSyncButton
                onSync={() => refetch()}
                lastUpdatedTime={lastUpdatedTime}
              />
              <MonitorExportButton onExport={handleMonitorExport} />
            </Flex>
          </Flex>
          <Flex className="usage-monitor-body" vertical gap={12}>
            <ConfigProvider
              theme={{
                algorithm: defaultAlgorithm,
                components: {
                  Table: {
                    headerBg: LIGHT_GRAY_COLOR,
                    colorBgContainer: WHITE_COLOR,
                    colorBorderSecondary: LIGHT_GRAY_COLOR,
                    headerColor: BLACK_COLOR,
                    headerSplitColor: TRANSPARENT,
                  },
                },
              }}
            >
              {Object.keys(sortedData).length != 0 && (
                <>
                  <Flex
                    vertical={false}
                    gap={4}
                    className="widget-row"
                    justify="space-between"
                  >
                    <Col className="widget-container">
                      <Flex
                        vertical={false}
                        align="center"
                        className="widget-title"
                        justify="space-between"
                      >
                        <div>{`Average Session Duration${selectedDate ? ` (${selectedDate})` : ''}`}</div>
                      </Flex>
                      <div className="widget-body">
                        <GroupedByUserGraph
                          data={
                            filteredAllData['FUNCTIONAL_DIAGRAM_UNLOADED'] || []
                          }
                          xFieldLabel={'Users'}
                          groupedValKey={'userTimeSpent'}
                          stat={'average'}
                          user_identifier={userIdentifier}
                          handleBarClick={(user) =>
                            setSelectedUser((prev) =>
                              user === prev ? null : user,
                            )
                          }
                        />
                      </div>
                    </Col>
                    <Col className="widget-container">
                      <Flex
                        vertical={false}
                        align="center"
                        className="widget-title"
                        justify="space-between"
                      >
                        <div>Daily Active Sessions</div>
                      </Flex>
                      <div className="widget-body">
                        <DailyActiveUsers
                          data={sortedData['FUNCTIONAL_DIAGRAM_UNLOADED']}
                          endDate={endTimeFilter}
                          onDateSelect={(date) =>
                            setSelectedDate((prev) =>
                              date === prev ? null : date,
                            )
                          }
                          selectedDate={selectedDate}
                        />
                      </div>
                    </Col>
                    <Col className="widget-container">
                      <Flex
                        vertical={false}
                        align="center"
                        className="widget-title"
                        justify="space-between"
                      >
                        <div>{`Viewed Components${selectedDate ? ` (${selectedDate})` : ''}`}</div>
                      </Flex>
                      <div className="widget-body">
                        <GroupedByUserGraph
                          data={[
                            ...(filteredAllData[
                              'FUNCTIONAL_DIAGRAM_COMPONENT_UPDATED'
                            ] || []),
                            ...(filteredAllData[
                              'FUNCTIONAL_DIAGRAM_CIRCUIT_UPDATED'
                            ] || []),
                            ...(filteredAllData[
                              'FUNCTIONAL_DIAGRAM_HARNESS_UPDATED'
                            ] || []),
                          ]}
                          xFieldLabel={'Users'}
                          yFieldLabel="Count"
                          groupedValKey={'timeOnComponent'}
                          detailKey={COMPONENT}
                          stat="count"
                          tooltipFunc={(val) => val}
                          user_identifier={userIdentifier}
                          handleBarClick={(user) => {
                            setShowDetailsOn(COMPONENT)
                            setSelectedUser((prev) =>
                              user === prev ? null : user,
                            )
                          }}
                        />
                      </div>
                    </Col>
                    <Col className="widget-container">
                      <Flex
                        vertical={false}
                        align="center"
                        className="widget-title"
                        justify="space-between"
                      >
                        <div>{`Viewed Subsystems by User${selectedDate ? ` (${selectedDate})` : ''}`}</div>
                      </Flex>
                      <div className="widget-body">
                        <GroupedByUserGraph
                          data={
                            filteredAllData[
                              'FUNCTIONAL_DIAGRAM_SUBSYSTEM_UPDATED'
                            ] || []
                          }
                          xFieldLabel={'Users'}
                          groupedValKey={'timeOnSubsystem'}
                          detailKey={SUBSYSTEM}
                          user_identifier={userIdentifier}
                          handleBarClick={(user) => {
                            setShowDetailsOn(SUBSYSTEM)
                            setSelectedUser((prev) =>
                              user === prev ? null : user,
                            )
                          }}
                        />
                      </div>
                    </Col>
                  </Flex>
                  <Flex
                    vertical={false}
                    gap={4}
                    className="widget-row"
                    justify="space-between"
                  >
                    <Col className="widget-container-wide">
                      <Flex
                        vertical={false}
                        align="center"
                        className="widget-title"
                        justify="space-between"
                      >
                        <div>
                          {selectedDate
                            ? `Daily Active Sessions by User (${selectedDate})`
                            : `Viewed ${
                                showDetailsOn === SUBSYSTEM
                                  ? 'Subsystems'
                                  : 'Components'
                              }`}
                        </div>
                        {!selectedDate && (
                          <Button
                            size="small"
                            type="text"
                            onClick={() => {
                              toggleModel(true)
                            }}
                          >
                            <EllipsisOutlined />
                          </Button>
                        )}
                      </Flex>
                      <div className="widget-body">
                        {selectedDate ? (
                          <GroupedByUserGraph
                            data={
                              filteredAllData['FUNCTIONAL_DIAGRAM_UNLOADED'] ||
                              []
                            }
                            xFieldLabel={'Users'}
                            yFieldLabel={'Sessions'}
                            groupedValKey={'timeOnFunctionalDiagrams'}
                            stat="count"
                            tooltipFunc={(val) => val}
                            user_identifier={userIdentifier}
                          />
                        ) : (
                          <MostViewedSubsystem
                            allData={filteredAllData}
                            showDetailsOn={showDetailsOn}
                          />
                        )}
                      </div>
                    </Col>
                    <Col className="widget-container-wide">
                      <Flex
                        vertical={false}
                        align="center"
                        className="widget-title"
                        justify="space-between"
                      >
                        <div>{`Time Spent on Subsystem${selectedDate ? ` (${selectedDate})` : ''}`}</div>
                      </Flex>
                      <div className="widget-body">
                        {sortedData['FUNCTIONAL_DIAGRAM_SUBSYSTEM_UPDATED'] && (
                          <TimeSpentOnSubsystem
                            data={
                              sortedData['FUNCTIONAL_DIAGRAM_SUBSYSTEM_UPDATED']
                            }
                          />
                        )}
                      </div>
                    </Col>
                  </Flex>
                </>
              )}
            </ConfigProvider>
            {isModalOpen && (
              <FullScreenSubsystemWidget
                toggleModalFunc={toggleModel}
                startTime={startTimeFilter}
                endTime={endTimeFilter}
                showDetailsOn={showDetailsOn}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    )
  }),
)
